/**
 * LA TABLE DE SIGNATURES DU PLAN — LE MONOCHROME D'ABORD.
 *
 * ⚠️⚠️ CE QUI A DÉCIDÉ CE FICHIER : LE PLAN N'ÉTAIT PAS LISIBLE SANS COULEUR.
 *
 * Mesuré le 22/09/2026 sur le style d'alors, en évaluant à la main chaque
 * `line-width` interpolé : **12 couples de classes partageaient le même couple
 * (épaisseur, motif) à z14 comme à z16, 9 à z18**. Des rapports d'épaisseur
 * exactement égaux à 1,00 :
 *
 *   z14  voirie desserte 3,0 px plein  ≡  limite de collectivité 3,0 px plein
 *   z16  voirie locale   3,4 px plein  ≡  limite de collectivité 3,4 px plein
 *   z16  section bornée  3,0 px plein  ≡  parcelle choisie       3,0 px plein
 *   z18  collectivité choisie 4,5 plein ≡ section choisie        4,5 plein
 *
 * Une rue et une limite de commune dessinées du même trait : toute
 * l'information reposait sur la TEINTE. Retirez la couleur — impression noir
 * et blanc, photocopie, agent daltonien, écran en plein soleil — et le plan
 * ne dit plus qui borne quoi.
 *
 * ⚠️ C'EST L'ORDRE DE TRAVAIL DES CADASTRES QUI SAVENT FAIRE. Chez swisstopo
 * (Weisung DMAV 1.0, 01/08/2024) chaque signature des tables 6 à 11 porte DEUX
 * colonnes normatives : « schwarz-weiss » ET « in Farbe (RGB) ». Chez ALKIS
 * Bavière, chaque page de la légende est « Legende farbig | Beschreibung |
 * Legende schwarz/weiß ». La couleur RENFORCE ; elle ne porte jamais seule.
 * C'est aussi pourquoi ces systèmes n'ont pas besoin d'un audit de ΔE : un plan
 * lisible sans couleur ne peut pas être cassé par une vision déficiente.
 *
 * ⚠️ ET LES ÉPAISSEURS SONT QUANTIFIÉES, comme les 0,15 / 0,20 / 0,25 / 0,30 /
 * 0,40 mm de swisstopo. Une valeur continue choisie couche par couche produit
 * mécaniquement des collisions que personne ne voit : 3,0 et 3,4 px se
 * ressemblent, et rien dans le code ne dit qu'elles devraient différer. Un
 * barreau d'échelle, lui, se compare.
 */

/**
 * L'ÉCHELLE DES ÉPAISSEURS — neuf barreaux, rapport 1,42 entre voisins.
 *
 * ⚠️ LE RAPPORT EST LE POINT. En deçà d'environ 1,35, deux traits de largeurs
 * différentes ne se distinguent pas à l'œil sur un parcellaire dense ; c'est ce
 * seuil qui a révélé les 12 collisions. À 1,42, deux barreaux voisins se
 * séparent toujours, et la garde peut donc se contenter de comparer des INDICES
 * de barreau au lieu de raisonner sur des pixels.
 */
export const ECHELLE = [0.65, 0.9, 1.3, 1.85, 2.6, 3.7, 5.3, 7.5, 10.6] as const;

/** Le vocabulaire des motifs. Un motif est une CLASSE, pas une nuance. */
export const MOTIFS = {
  plein: undefined,
  /** La convention administrative — celle du Gemeindegrenze suisse. */
  tiret_point: [6, 2, 1, 2],
  tirets_longs: [8, 3],
  tirets: [4, 2],
  tirets_courts: [2, 1.5],
  pointille: [1, 2],
} as const;

export type Motif = keyof typeof MOTIFS;

/**
 * ⚠️ DEUX FAMILLES, ET LA GARDE NE LES TRAITE PAS PAREIL.
 *
 * `classe` — ce qui porte de l'INFORMATION : le lecteur doit pouvoir décoder
 * la nature d'un trait sans rien savoir d'autre. Deux classes ne peuvent JAMAIS
 * partager le même couple.
 *
 * `choix` — un surlignage transitoire, commandé par le lecteur lui-même. Il
 * n'a rien à décoder : il vient de cliquer. Un choix doit seulement se
 * DÉTACHER de la classe qu'il recouvre, d'au moins un barreau et demi.
 */
export type Famille = 'classe' | 'choix';

export interface Signature {
  /** Ce que le trait dit, en français, tel qu'il doit apparaître en légende. */
  readonly libelle: string;
  readonly famille: Famille;
  /** Indice dans `ECHELLE`. */
  readonly barreau: number;
  readonly motif: Motif;
  /** Pour un `choix` : la clé de la classe qu'il surligne. */
  readonly surligne?: string;
  /**
   * ⚠️ LE ZOOM D'APPARITION FAIT PARTIE DE LA SIGNATURE, et pas seulement du
   * style. Sans lui, la garde de finesse jugeait les emprises à z9 — un niveau
   * où elles ne sont pas dessinées. Une classe absente ne peut pas être trop
   * fine : c'était un faux positif de la garde, pas un défaut du plan.
   */
  readonly depuis: number;
}

/**
 * LES SIGNATURES, DU TRAIT LE PLUS LOURD AU PLUS LÉGER.
 *
 * ⚠️ LA COLLECTIVITÉ PREND LE TIRET-POINT, ET CE N'EST PAS DÉCORATIF. Elle
 * entrait en collision exacte avec la voirie — 3,0 px plein contre 3,0 px plein
 * à z14, 3,4 contre 3,4 à z16 — parce que les deux familles se disputaient les
 * mêmes barreaux hauts. Un motif propre les sépare à TOUTE épaisseur, ce
 * qu'aucun réglage de largeur ne pouvait garantir. Et c'est la convention :
 * swisstopo donne aux quatre échelons de souveraineté la même couleur et la
 * même épaisseur, et les distingue par leur seul motif de tiret.
 *
 * ⚠️ La collectivité reste néanmoins le trait de LIMITE le plus lourd : c'est
 * la clôture dans laquelle un code de section devient unique — 1 515 codes sur
 * 1 552 sont portés par plusieurs collectivités (mesure du 21/09, session
 * mowibo-ia-be). La voirie n'est pas une limite : c'est une emprise dessinée
 * en gaine, d'où ses barreaux hauts sans conflit de sens.
 */
export const SIGNATURES: Readonly<Record<string, Signature>> = {
  voirie_structurante: { libelle: 'voirie structurante', famille: 'classe', barreau: 8, motif: 'plein', depuis: 9 },
  voirie_desserte:     { libelle: 'voirie de desserte',  famille: 'classe', barreau: 7, motif: 'plein', depuis: 9 },
  voirie_locale:       { libelle: 'voirie locale',       famille: 'classe', barreau: 6, motif: 'plein', depuis: 9 },

  collectivite:        { libelle: 'limite de collectivité', famille: 'classe', barreau: 5, motif: 'tiret_point', depuis: 4 },

  division_1:          { libelle: 'limite administrative',  famille: 'classe', barreau: 4, motif: 'tirets_longs', depuis: 0 },
  division_4:          { libelle: 'limite de quartier',     famille: 'classe', barreau: 1, motif: 'tirets_longs', depuis: 0 },

  section_bornee:      { libelle: 'section — bornée par les rues', famille: 'classe', barreau: 3, motif: 'plein', depuis: 11 },
  section_carroyage:   { libelle: 'section — tracée au carroyage', famille: 'classe', barreau: 3, motif: 'tirets', depuis: 11 },

  parcelle:            { libelle: 'limite de parcelle',     famille: 'classe', barreau: 2, motif: 'plein', depuis: 13 },

  emprise_rattachee:   { libelle: 'emprise rattachée',      famille: 'classe', barreau: 0, motif: 'plein', depuis: 14 },
  emprise_orpheline:   { libelle: 'emprise non rattachée',  famille: 'classe', barreau: 0, motif: 'tirets_courts', depuis: 14 },

  /* Les surlignages. Deux barreaux au-dessus de leur classe : ils doivent se
   * voir par-dessous le curseur, pas se décoder. */
  choix_collectivite:  { libelle: 'collectivité ouverte', famille: 'choix', barreau: 7, motif: 'plein', surligne: 'collectivite', depuis: 4 },
  choix_section:       { libelle: 'section ouverte',      famille: 'choix', barreau: 5, motif: 'plein', surligne: 'section_bornee', depuis: 11 },
  choix_parcelle:      { libelle: 'parcelle ouverte',     famille: 'choix', barreau: 4, motif: 'plein', surligne: 'parcelle', depuis: 13 },
  /* La sélection de la TABLE (D.948) : plusieurs objets à la fois, motif
   * tireté pour ne pas se confondre avec la parcelle OUVERTE (plein). */
  choix_table:         { libelle: 'sélection de la table', famille: 'choix', barreau: 4, motif: 'tirets', surligne: 'parcelle', depuis: 13 },
  /* Le cadre du tirage (D.980) : transitoire — il n'existe que pendant que le
   * bloc d'impression est ouvert, et il dessine CE QUI SORTIRA. Motif tireté
   * long : un cadre plein se lirait comme une limite du registre. */
  cadre_feuille:       { libelle: 'cadre du tirage',      famille: 'choix', barreau: 6, motif: 'tirets_longs', surligne: 'section_bornee', depuis: 11 },
};

/**
 * LE FACTEUR DE ZOOM — UN SEUL, PARTAGÉ PAR TOUTES LES SIGNATURES.
 *
 * ⚠️⚠️ C'EST CE PARTAGE QUI REND L'INVARIANT VÉRIFIABLE UNE FOIS POUR TOUTES.
 * Chaque couche portait sa propre interpolation de zoom ; les rapports entre
 * classes changeaient donc d'un niveau à l'autre, et deux traits distincts à
 * z14 pouvaient se confondre à z16 — c'est précisément ce qui arrivait à la
 * voirie et à la collectivité, qui se croisaient entre les deux. Avec un
 * facteur commun, les rapports sont INVARIANTS par zoom : prouver l'échelle
 * suffit à prouver le plan à tous les niveaux, au lieu d'échantillonner.
 */
export const FACTEUR_ZOOM = { 9: 0.40, 13: 0.70, 16: 1.0, 18: 1.45, 20: 1.9 } as const;

/** Largeur rendue d'une signature à un zoom donné, en pixels. */
export function largeur(cle: string, zoom: number): number {
  const sig = SIGNATURES[cle];
  if (sig === undefined) throw new Error(`signature inconnue : ${cle}`);
  const base = ECHELLE[sig.barreau];
  if (base === undefined) throw new Error(`barreau hors échelle pour ${cle} : ${sig.barreau}`);
  const paliers = Object.keys(FACTEUR_ZOOM).map(Number).sort((a, b) => a - b);
  const valeur = (z: number) => FACTEUR_ZOOM[z as keyof typeof FACTEUR_ZOOM];
  const premier = paliers[0]!;
  const dernier = paliers[paliers.length - 1]!;
  if (zoom <= premier) return base * valeur(premier);
  if (zoom >= dernier) return base * valeur(dernier);
  for (let i = 0; i + 1 < paliers.length; i++) {
    const a = paliers[i]!;
    const b = paliers[i + 1]!;
    if (zoom >= a && zoom <= b) {
      const t = (zoom - a) / (b - a);
      return base * (valeur(a) + t * (valeur(b) - valeur(a)));
    }
  }
  return base;
}

/**
 * L'expression MapLibre de `line-width` pour une signature.
 * Le facteur de zoom est déplié en paliers : MapLibre interpole, et le
 * résultat est identique à `largeur()` — c'est ce que la garde vérifie.
 */
export function expressionLargeur(cle: string): unknown[] {
  const sig = SIGNATURES[cle];
  if (sig === undefined) throw new Error(`signature inconnue : ${cle}`);
  const base = ECHELLE[sig.barreau]!;
  const paliers: unknown[] = ['interpolate', ['linear'], ['zoom']];
  for (const z of Object.keys(FACTEUR_ZOOM).map(Number).sort((a, b) => a - b)) {
    paliers.push(z, Number((base * FACTEUR_ZOOM[z as keyof typeof FACTEUR_ZOOM]).toFixed(3)));
  }
  return paliers;
}

/**
 * Le motif d'une signature, prêt pour `line-dasharray`.
 *
 * ⚠️ REND `undefined` POUR UN TRAIT PLEIN, ET L'APPELANT DOIT OMETTRE LA
 * PROPRIÉTÉ. Poser `line-dasharray: []` n'est pas « pas de tiret » : MapLibre
 * refuse le tableau vide, et il refuse le style ENTIER — pas la couche.
 */
export function motifDe(cle: string): readonly number[] | undefined {
  const sig = SIGNATURES[cle];
  if (sig === undefined) throw new Error(`signature inconnue : ${cle}`);
  return MOTIFS[sig.motif];
}

/** Le couple qui doit être unique parmi les classes. */
export const coupleDe = (cle: string): string => {
  const sig = SIGNATURES[cle];
  if (sig === undefined) throw new Error(`signature inconnue : ${cle}`);
  return `${sig.barreau}/${sig.motif}`;
};

/**
 * LARGEUR PILOTÉE PAR UN ATTRIBUT — pour les classes qui forment une rampe
 * dans une même famille : les trois rangs de voirie, les niveaux de division.
 *
 * ⚠️⚠️ `line-width` EST PILOTABLE PAR LES DONNÉES, `line-dasharray` NE L'EST
 * PAS. La spécification MapLibre déclare `line-dasharray` « cross-faded » : un
 * `['match', ['get', …]]` posé dessus fait refuser le style **ENTIER**, pas la
 * couche — panne de production déjà payée le 19/09/2026, carte noire, et
 * `tests/style-carte.test.ts` la tient depuis. Deux classes qui ne diffèrent
 * que par le motif exigent donc deux COUCHES filtrées ; deux classes qui ne
 * diffèrent que par l'épaisseur peuvent partager une couche. C'est cette
 * asymétrie que cette fonction sert, et c'est pourquoi les signatures
 * regroupées ici doivent toutes porter le même motif — la garde le vérifie.
 */
export function expressionLargeurSelon(
  attribut: string,
  cas: ReadonlyArray<readonly [valeur: string | number, cle: string]>,
  repli: string,
): unknown[] {
  const zooms = Object.keys(FACTEUR_ZOOM).map(Number).sort((a, b) => a - b);
  const expr: unknown[] = ['interpolate', ['linear'], ['zoom']];
  for (const z of zooms) {
    const branche: unknown[] = ['case'];
    for (const [valeur, cle] of cas) {
      branche.push(['==', ['get', attribut], valeur], Number(largeur(cle, z).toFixed(3)));
    }
    branche.push(Number(largeur(repli, z).toFixed(3)));
    expr.push(z, branche);
  }
  return expr;
}

/** Les signatures regroupées dans une même couche : même motif obligatoire. */
export const GROUPES: Readonly<Record<string, readonly string[]>> = {
  voirie: ['voirie_structurante', 'voirie_desserte', 'voirie_locale'],
  division: ['division_1', 'division_4'],
};

/**
 * LA GAINE D'UNE CLASSE — un halo tracé sous un trait pour le détacher du fond.
 *
 * ⚠️ CE N'EST PAS UNE CLASSE D'INFORMATION, et la garde ne l'inventorie donc
 * pas : rien ne se décode dans un halo. Mais il doit SUIVRE sa classe. Il
 * portait ses propres nombres (3,5 / 9 / 20 px contre 1,6 / 5 / 14 pour la
 * voirie) : changer l'échelle des signatures sans y penser aurait fait déborder
 * la gaine sous le trait, ou disparaître le trait dans sa gaine. Elle est donc
 * CALCULÉE — un multiple, plus une marge constante en pixels d'écran.
 */
export const GAINE_FACTEUR = 1.35;
export const GAINE_MARGE = 1.6;

export function expressionGaineSelon(
  attribut: string,
  cas: ReadonlyArray<readonly [valeur: string | number, cle: string]>,
  repli: string,
): unknown[] {
  const gaine = (cle: string, z: number): number =>
    Number((largeur(cle, z) * GAINE_FACTEUR + GAINE_MARGE).toFixed(3));
  const expr: unknown[] = ['interpolate', ['linear'], ['zoom']];
  for (const z of Object.keys(FACTEUR_ZOOM).map(Number).sort((a, b) => a - b)) {
    const branche: unknown[] = ['case'];
    for (const [valeur, cle] of cas) {
      branche.push(['==', ['get', attribut], valeur], gaine(cle, z));
    }
    branche.push(gaine(repli, z));
    expr.push(z, branche);
  }
  return expr;
}
